import Subscription from '@aytm/lib/common/subscription'
import railsPostFetch from '@aytm/lib/common/rails-post-fetch'

import { ChatMessage } from '../../types'
import { SupportChatAssistant as TSupportChatAssistant, SupportChatAssistantState } from './types'
import { DEFAULT_POLL_MS, IDLE_POLL_MS } from '../common/const'
import ChatError from '../../common/ChatError'
import buildPollingService from '../common/pollingService'
import awaitContext from './lib/awaitContext'

import initAction from '../common/init'
import sendAction from '../common/send'
import commandAction from '../common/command'
import rateMessageAction from '../common/rateMessage'

import supportChatAssistantMessageExtensions from './messageExtensions'

import SupportGreetingCard from './SupportStubAssistantGreetingCard'
import SupportOfflineGreetingCard from './SupportOfflineGreetingCard'
import { isSupportAvailable, clearWorkingHoursCache } from './lib/workingHours'

export default function SupportChatAssistant(): TSupportChatAssistant {
  const assistantState: SupportChatAssistantState = {
    ready: false,
    opened: false,
    inputReady: false,
    closed: false,
    rated: false,
    offline: false,
    offlineMessageSent: false,
    offlineTransitionNotified: false,
    chatID: null,
    userName: null,
    limits: null,
    messages: [],
    hasUnreadMessages: false,
    currentPrompt: '',
    connectionStatus: 'ok',
    contextGetter: null,
    pollingService: null,
    subscription: null,
    activeAgent: null,
    agentAvatars: new Map()
  }

  let assistantInitializing = false
  const readMessageIDs: Set<string> = new Set()
  let followupMode = false

  let assistant: TSupportChatAssistant = null

  const AGENT_BLOCK_RE = /~~~(support_agent(?:_assigned|_reassigned|_left)?)\s*\n([\s\S]+?)\n~~~/g

  const deriveAgentState = () => {
    const next = new Map<string, string>()
    let nextActive: { name: string; avatar: string } | null = null
    for (const message of assistantState.messages) {
      AGENT_BLOCK_RE.lastIndex = 0
      let match: RegExpExecArray | null
      while ((match = AGENT_BLOCK_RE.exec(message.text)) !== null) {
        const blockType = match[1]
        let parsed: { name?: string; avatar?: string } | null
        try {
          parsed = JSON.parse(match[2]) as { name?: string; avatar?: string } | null
        } catch {
          continue
        }
        if (!parsed?.name) continue
        const avatar = parsed.avatar ?? ''
        next.set(parsed.name, avatar)
        if (blockType !== 'support_agent_left') {
          nextActive = { name: parsed.name, avatar }
        }
      }
    }
    // In-place rebuild — preserves Map reference for React.memo in ChatMessage.
    assistantState.agentAvatars.clear()
    for (const [name, avatar] of next) {
      assistantState.agentAvatars.set(name, avatar)
    }
    assistantState.activeAgent = nextActive
  }

  // mapMessages produces a new array ref whenever the list changes, so the
  // `!==` check is precise; unrelated update()s (closed/inputReady flips) skip.
  let lastSeenMessages: typeof assistantState.messages | null = null
  const update: () => void = () => {
    if (lastSeenMessages !== assistantState.messages) {
      deriveAgentState()
      lastSeenMessages = assistantState.messages
    }
    if (!assistantState.opened && !assistantState.hasUnreadMessages) {
      assistantState.hasUnreadMessages = assistantState.messages.some(message => !readMessageIDs.has(message.id))
      if (assistantState.hasUnreadMessages) {
        // eslint-disable-next-line no-console
        console.debug('SupportChatAssistant: has unread messages', assistant.id)
      }
    } else {
      assistantState.hasUnreadMessages = false
    }
    assistantState.subscription?.update(assistant)
  }

  const handleSupportAction = (event: CustomEvent) => {
    const { action } = event.detail as { action: string }
    switch (action) {
      case 'add_followup':
        followupMode = true
        // assistantState.inputReady = true
        update()
        break
      case 'email_chat_log':
        // eslint-disable-next-line no-console
        console.debug('Email chat log requested')
        break
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('support-chat-action', handleSupportAction as EventListener)
  }

  assistantState.pollingService = buildPollingService({
    assistantState,
    update,
    awaitContext
  }) as typeof assistantState.pollingService

  assistant = {
    get id() {
      return 'support'
    },
    get name() {
      return assistantState.activeAgent && !assistantState.closed ? assistantState.activeAgent.name : 'Human Support'
    },
    get icon() {
      return 'icon-aytmfd-skipper-support'
    },
    get activeAgent() {
      return assistantState.activeAgent
    },
    get ready() {
      return assistantState.ready
    },
    async init(newChat = false) {
      if (assistantInitializing) {
        return new ChatError('Chat assistant is already initializing')
      }

      if ('development' === process.env.NODE_ENV) {
        // eslint-disable-next-line no-console
        console.debug('SupportChatAssistant: Starting initialization', {
          newChat,
          contextGetter: Boolean(assistantState.contextGetter)
        })
      }

      assistantInitializing = true
      followupMode = false

      // Reset before initAction's early update() — that update fires before
      // messages are replaced, so derivation alone would leave stale agent visible.
      assistantState.activeAgent = null
      assistantState.agentAvatars.clear()

      // Clear cache on init so new tabs get fresh working hours
      clearWorkingHoursCache()
      assistantState.offline = !(await isSupportAvailable())
      assistantState.offlineMessageSent = false
      assistantState.offlineTransitionNotified = false
      if ('development' === process.env.NODE_ENV) {
        // eslint-disable-next-line no-console
        console.debug('SupportChatAssistant: offline mode', assistantState.offline)
      }

      try {
        const initResult = await initAction({
          assistantState,
          update,
          newChat,
          domain: 'support',
          awaitContext,
          debugName: 'SupportChatAssistant'
        })
        assistantInitializing = false

        if ('development' === process.env.NODE_ENV) {
          // eslint-disable-next-line no-console
          console.debug('SupportChatAssistant: Initialization successful', initResult)
        }

        return initResult
      } catch (error) {
        assistantInitializing = false

        if ('development' === process.env.NODE_ENV) {
          // eslint-disable-next-line no-console
          console.error('SupportChatAssistant: Initialization failed', error)
        }

        throw error
      }
    },
    async send(message: ChatMessage | string) {
      if (assistantState.ready || followupMode) {
        const originalContextGetter = assistantState.contextGetter
        if (followupMode && assistantState.contextGetter) {
          const originalContext = assistantState.contextGetter()
          assistantState.contextGetter = () => {
            return { ...originalContext, followup: true }
          }
        }

        let result: ChatError | undefined

        if (assistantState.offline) {
          const messageText = typeof message === 'string' ? message : message.text
          const attachments = typeof message !== 'string' ? message.attachments : undefined
          result = await commandAction(
            'create_offline_support_ticket',
            { message: messageText, attachments: attachments || [] },
            {
              assistantState,
              update,
              awaitContext,
              debugName: 'SupportChatAssistant'
            }
          )

          if (!(result instanceof ChatError)) {
            assistantState.offlineMessageSent = true
            update()
          }
        } else {
          result = await sendAction(message, {
            assistantState,
            update,
            awaitContext,
            debugName: 'SupportChatAssistant'
          })

          if (!(result instanceof ChatError)) {
            const isAvailable = await isSupportAvailable()
            if (!isAvailable) {
              await commandAction(
                'add_offline_message',
                {},
                {
                  assistantState,
                  update,
                  awaitContext,
                  debugName: 'SupportChatAssistant'
                }
              )
            }
          }
        }

        if (followupMode && originalContextGetter) {
          assistantState.contextGetter = originalContextGetter
          followupMode = false
        }

        return result
      } else {
        const error: ChatError = new ChatError('Chat assistant is not ready')
        return error
      }
    },
    async command(command, payload = {}) {
      if (assistantState.ready || followupMode) {
        const originalContextGetter = assistantState.contextGetter
        if (followupMode && assistantState.contextGetter) {
          const originalContext = assistantState.contextGetter()
          assistantState.contextGetter = () => {
            return { ...originalContext, followup: true }
          }
        }

        const result = await commandAction(command, payload, {
          assistantState,
          update,
          awaitContext,
          debugName: 'SupportChatAssistant'
        })

        if (followupMode && originalContextGetter) {
          assistantState.contextGetter = originalContextGetter
          followupMode = false
        }

        return result
      } else {
        const error: ChatError = new ChatError('Chat assistant is not ready')
        return error
      }
    },
    async rateMessage(messageID, like) {
      if (assistantState.ready) {
        return await rateMessageAction(messageID, like, {
          assistantState,
          update,
          awaitContext,
          debugName: 'SupportChatAssistant'
        })
      } else {
        const error: ChatError = new ChatError('Chat assistant is not ready')
        return error
      }
    },
    get canRateMessage() {
      return true
    },
    get GreetingCard() {
      return assistantState.offline ? SupportOfflineGreetingCard : SupportGreetingCard
    },
    get inputPlaceholder() {
      if (assistantState.offline) {
        return 'Leave us a message'
      }
      return followupMode ? 'Add a follow-up message...' : 'Describe your issue...'
    },
    get currentPrompt() {
      return assistantState.currentPrompt
    },
    set currentPrompt(prompt) {
      assistantState.currentPrompt = prompt
    },
    get chatID() {
      return assistantState.chatID
    },
    set chatID(chatID) {
      assistantState.chatID = chatID
      update()
    },
    get userName() {
      return assistantState.userName
    },
    set userName(userName) {
      assistantState.userName = userName
      update()
    },
    get conversationLimits() {
      return assistantState.limits
    },
    get inputReady() {
      return assistantState.inputReady
    },
    get closed(): boolean {
      return assistantState.closed
    },
    get rated(): boolean {
      return assistantState.rated
    },
    get offline(): boolean {
      return assistantState.offline
    },
    get offlineMessageSent(): boolean {
      return assistantState.offlineMessageSent
    },
    setOfflineMessageSent(sent: boolean) {
      assistantState.offlineMessageSent = sent
      update()
    },
    get messages() {
      return assistantState.messages
    },
    get messageExtensions() {
      return supportChatAssistantMessageExtensions
    },
    get agentAvatars() {
      return assistantState.agentAvatars
    },
    get connectionStatus() {
      return assistantState.connectionStatus
    },
    get hasUnreadMessages() {
      return !assistantState.opened && assistantState.hasUnreadMessages
    },
    get suggestedPrompts(): null {
      return null
    },
    get contextGetter() {
      return assistantState.contextGetter
    },
    set contextGetter(contextGetter) {
      assistantState.contextGetter = contextGetter
    },
    get acceptAttachments() {
      return true
    },
    get currentDomain() {
      return 'support'
    },
    get currentContextId() {
      return assistantState.contextGetter?.()?.survey_id ?? null
    },
    async chatOpened() {
      assistantState.opened = true
      readMessageIDs.clear()
      if (IDLE_POLL_MS === assistantState.pollingService.interval) {
        assistantState.pollingService.interval = DEFAULT_POLL_MS
      }

      const isAvailable = await isSupportAvailable()
      if (assistantState.offline === isAvailable) {
        assistantState.offline = !isAvailable
        assistantState.offlineMessageSent = false
        assistantState.offlineTransitionNotified = false
        if ('development' === process.env.NODE_ENV) {
          // eslint-disable-next-line no-console
          console.debug('SupportChatAssistant: offline mode changed', assistantState.offline)
        }
        update()
      }

      if (assistantState.hasUnreadMessages) {
        assistantState.hasUnreadMessages = false
        update()
      }
    },
    chatClosed() {
      assistantState.opened = false
      readMessageIDs.clear()
      assistantState.messages.forEach(message => void readMessageIDs.add(message.id))
      if (assistantState.pollingService.interval > 0) {
        assistantState.pollingService.interval = IDLE_POLL_MS
      }
      assistantState.offlineTransitionNotified = false
    },
    listen(listener) {
      if (!assistantState.subscription) {
        assistantState.subscription = Subscription<TSupportChatAssistant>()
      }
      return assistantState.subscription.listen(listener, assistant)
    },
    update() {
      update()
    },
    async rateChat(like: boolean, message?: string) {
      if (!assistantState.chatID) {
        return new ChatError('No chat to rate')
      }

      if (assistantState.rated) {
        return new ChatError('Chat already rated')
      }

      try {
        const response = await railsPostFetch(`/api/v1/skipper/chats/${assistantState.chatID}/rate`, {
          method: 'PUT',
          body: JSON.stringify({ like, message })
        })

        if (!response.ok) {
          return new ChatError('Failed to rate chat')
        }

        const data = (await response.json()) as { status: 'ok' | 'error' }

        if (data.status !== 'ok') {
          return new ChatError('Failed to rate chat')
        }

        assistantState.rated = true
        update()
        return undefined // eslint-disable-line unicorn/no-useless-undefined
      } catch {
        return new ChatError('Failed to rate chat')
      }
    },
    async endSession() {
      if (!assistantState.ready || assistantState.closed) {
        return new ChatError('Cannot end session')
      }
      // Optimistic close — composer hides instantly; if command fails, polling
      // surfaces the actual server state and the UI corrects itself.
      assistantState.closed = true
      update()
      return await commandAction(
        'end_session',
        {},
        { assistantState, update, awaitContext, debugName: 'SupportChatAssistant' }
      )
    },
    destroy() {
      if (assistantState.subscription) {
        assistantState.subscription.clear()
        assistantState.subscription = null
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('support-chat-action', handleSupportAction as EventListener)
      }
    }
  }

  return assistant
}
